import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { AccountTypeBadge } from "../../../Common/TreeView";
import {
  ChartOfAccountStyles,
  money,
  num,
} from "../Chart-Of-Account/ChartOfAccountShared";
import { typeLabel } from "./SubCategoryForm";

// This endpoint computes balance as opening + debit - credit, so a positive
// figure is a debit balance.
const BalanceCell = ({ value, strong }) => {
  const amount = num(value);
  return (
    <span
      className="coa-num"
      style={{
        display: "block",
        fontWeight: strong ? 700 : 600,
        color: amount ? "#2a2962" : "#b6bacb",
      }}
    >
      {money(Math.abs(amount))}
      {amount !== 0 && (
        <span
          className={`coa-pill mg-l-6 ${
            amount > 0 ? "coa-pill-dr" : "coa-pill-cr"
          }`}
        >
          {amount > 0 ? "DR" : "CR"}
        </span>
      )}
    </span>
  );
};

const AmountCell = ({ value, strong }) => {
  const amount = num(value);
  return (
    <span
      className="coa-num"
      style={{
        display: "block",
        fontWeight: strong ? 600 : 400,
        color: amount ? "#35374d" : "#b6bacb",
      }}
    >
      {money(amount)}
    </span>
  );
};

const List = () => {
  const location = useLocation();
  const [groups, setGroups] = useState([]);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [collapsed, setCollapsed] = useState(new Set());

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await Http.get(`/sub-category-list-show`);
      setGroups(res.data || []);
      setCollapsed(new Set());
    } catch (e) {
      console.error("Fetch error:", e);
      setError("Could not load sub categories. Please refresh and try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const showAlert = (message, type) => {
    setAlert({ message: message, type: type });
    setTimeout(() => setAlert(null), 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  // A category stays visible when the category itself matches, so a search on
  // the COA name shows every sub-category underneath it.
  const filteredGroups = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) {
      return groups;
    }

    const hit = (value) =>
      String(value ?? "")
        .toLowerCase()
        .includes(term);

    return groups
      .map((group) => {
        if (hit(group.coa_name) || hit(group.coa_code)) {
          return group;
        }

        const detail = (group.detail || []).filter(
          (row) => hit(row.name) || hit(row.code) || hit(row.remarks),
        );

        return detail.length > 0 ? { ...group, detail } : null;
      })
      .filter(Boolean);
  }, [groups, searchTerm]);

  const stats = useMemo(() => {
    const rows = groups.flatMap((group) => group.detail || []);
    return {
      categories: groups.length,
      subCategories: rows.length,
      inactive: rows.filter((row) => row.status && row.status !== "active")
        .length,
      debit: groups.reduce((sum, g) => sum + num(g.total_debit), 0),
      credit: groups.reduce((sum, g) => sum + num(g.total_credit), 0),
      balance: groups.reduce((sum, g) => sum + num(g.balance), 0),
    };
  }, [groups]);

  const toggleGroup = (id) => {
    const next = new Set(collapsed);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setCollapsed(next);
  };

  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () =>
    setCollapsed(new Set(groups.map((g) => g.acc_chart_of_account_id)));

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <style>
          {`
            .sc-group-row { background: #f5f6fb; cursor: pointer; }
            .sc-group-row:hover { background: #eef0f8; }
            .sc-group-row td { border-top: 1px solid #e6e8f0; }
            .sc-toggle {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 20px;
              height: 20px;
              border-radius: 5px;
              background: #2a2962;
              color: #fff;
              font-size: 0.65rem;
              margin-right: 10px;
            }
            .sc-child-name {
              position: relative;
              padding-left: 42px !important;
            }
            /* Elbow from the category's toggle down to each sub category. */
            .sc-child-name::before {
              content: "";
              position: absolute;
              left: 26px;
              top: 0;
              bottom: 0;
              border-left: 1px solid #c9ccdc;
            }
            .sc-child-name.is-last::before { bottom: 50%; }
            .sc-child-name::after {
              content: "";
              position: absolute;
              left: 26px;
              top: 50%;
              width: 10px;
              border-top: 1px solid #c9ccdc;
            }
          `}
        </style>
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">Sub Categories</h5>
            <p className="text-muted mb-0 tx-12">
              Party-level balances grouped under their chart of account
            </p>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={fetchData}
              title="Reload data"
            >
              <i className="fa fa-refresh"></i>
            </button>
            <Link to="/create-sub-category" className="btn btn-primary">
              <i className="fa fa-plus-square mg-r-6"></i>New Sub Category
            </Link>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="row mg-b-20">
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-sitemap coa-stat-icon"></i>
                <span className="coa-stat-label">Sub Categories</span>
                <span className="coa-stat-value">{stats.subCategories}</span>
                <span className="coa-stat-sub">
                  Under {stats.categories}{" "}
                  {stats.categories === 1 ? "account" : "accounts"}
                  {stats.inactive > 0 && ` · ${stats.inactive} inactive`}
                </span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-arrow-circle-o-down coa-stat-icon"></i>
                <span className="coa-stat-label">Total Debit</span>
                <span className="coa-stat-value">{money(stats.debit)}</span>
                <span className="coa-stat-sub">All sub categories</span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-arrow-circle-o-up coa-stat-icon"></i>
                <span className="coa-stat-label">Total Credit</span>
                <span className="coa-stat-value">{money(stats.credit)}</span>
                <span className="coa-stat-sub">All sub categories</span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-balance-scale coa-stat-icon"></i>
                <span className="coa-stat-label">Net Balance</span>
                <span className="coa-stat-value">
                  {money(Math.abs(stats.balance))}{" "}
                  <small>{stats.balance >= 0 ? "DR" : "CR"}</small>
                </span>
                <span className="coa-stat-sub">Including opening balances</span>
              </div>
            </div>
          </div>

          {error && (
            <div className="coa-notice is-alert">
              <i className="fa fa-exclamation-triangle"></i>
              <span className="flex-grow-1">{error}</span>
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={fetchData}
              >
                Retry
              </button>
            </div>
          )}

          <div className="coa-panel">
            <div className="coa-panel-head">
              <div className="d-flex align-items-center flex-wrap gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={expandAll}
                  disabled={collapsed.size === 0}
                >
                  <i className="fa fa-plus-square-o mg-r-6"></i>Expand All
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={collapseAll}
                  disabled={collapsed.size === groups.length}
                >
                  <i className="fa fa-minus-square-o mg-r-6"></i>Collapse All
                </button>
              </div>
              <div className="input-group" style={{ maxWidth: "340px" }}>
                <span className="input-group-text">
                  <i className="fa fa-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search account or sub category"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {loading ? (
              <div className="coa-empty">
                <i className="fa fa-spinner fa-spin"></i>
                <h6>Loading sub categories</h6>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table coa-table">
                  <thead>
                    <tr>
                      <th>Account / Sub Category</th>
                      <th>Code</th>
                      <th>Type</th>
                      <th className="coa-num">Opening</th>
                      <th className="coa-num">Debit</th>
                      <th className="coa-num">Credit</th>
                      <th className="coa-num">Balance</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredGroups.length === 0 && (
                      <tr>
                        <td colSpan="8">
                          <div className="coa-empty">
                            <i className="fa fa-folder-open-o"></i>
                            {searchTerm
                              ? `No sub categories match "${searchTerm}".`
                              : "No sub categories yet."}
                          </div>
                        </td>
                      </tr>
                    )}

                    {filteredGroups.map((group) => {
                      const isCollapsed = collapsed.has(
                        group.acc_chart_of_account_id,
                      );
                      const detail = group.detail || [];

                      return (
                        <React.Fragment key={group.acc_chart_of_account_id}>
                          {/* Category header: every sub-category below belongs to it. */}
                          <tr
                            className="sc-group-row"
                            onClick={() =>
                              toggleGroup(group.acc_chart_of_account_id)
                            }
                          >
                            <td>
                              <span className="sc-toggle">
                                <i
                                  className={`fa ${
                                    isCollapsed ? "fa-plus" : "fa-minus"
                                  }`}
                                ></i>
                              </span>
                              <span
                                style={{ fontWeight: 700, color: "#2a2962" }}
                              >
                                {group.coa_name}
                              </span>
                              <span className="coa-chip-count mg-l-8">
                                {detail.length}
                              </span>
                            </td>
                            <td>
                              <span className="coa-code">{group.coa_code}</span>
                            </td>
                            <td>
                              <AccountTypeBadge type={group.coa_account_type} />
                            </td>
                            <td>
                              <AmountCell
                                value={group.opening_balance}
                                strong
                              />
                            </td>
                            <td>
                              <AmountCell value={group.total_debit} strong />
                            </td>
                            <td>
                              <AmountCell value={group.total_credit} strong />
                            </td>
                            <td>
                              <BalanceCell value={group.balance} strong />
                            </td>
                            <td></td>
                          </tr>

                          {!isCollapsed &&
                            detail.map((row, index) => (
                              <tr key={row.id}>
                                <td
                                  className={`sc-child-name${
                                    index === detail.length - 1
                                      ? " is-last"
                                      : ""
                                  }`}
                                >
                                  <span style={{ color: "#35374d" }}>
                                    {row.name}
                                  </span>
                                  {row.status && row.status !== "active" && (
                                    <span className="coa-pill coa-pill-muted mg-l-8">
                                      {row.status}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {row.code ? (
                                    <span className="coa-code">{row.code}</span>
                                  ) : (
                                    <span style={{ color: "#b6bacb" }}>—</span>
                                  )}
                                </td>
                                <td>
                                  <span className="coa-pill coa-pill-dr">
                                    {typeLabel(row.type)}
                                  </span>
                                </td>
                                <td>
                                  <AmountCell value={row.opening_balance} />
                                </td>
                                <td>
                                  <AmountCell value={row.total_debit} />
                                </td>
                                <td>
                                  <AmountCell value={row.total_credit} />
                                </td>
                                <td>
                                  <BalanceCell value={row.balance} />
                                </td>
                                <td>
                                  <div className="d-flex gap-1 justify-content-center">
                                    <Link
                                      to={"/edit-sub-category/" + row.id}
                                      className="btn btn-outline-secondary btn-sm"
                                      title="Edit sub category"
                                    >
                                      <i
                                        className="fa fa-pencil"
                                        aria-hidden="true"
                                      ></i>
                                    </Link>
                                    <Link
                                      to={"/sub-category-ledger/" + row.id}
                                      className="btn btn-outline-secondary btn-sm"
                                      title="View ledger"
                                    >
                                      <i
                                        className="fa fa-list"
                                        aria-hidden="true"
                                      ></i>
                                    </Link>
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default List;
